import React from "react";
import { useNavigate } from "react-router-dom";

const challengeItems = [
  {
    title: "Financing Gap",
    description:
      "Limited loan availability for MSMEs leaves many entrepreneurs without the working capital needed to stabilize and grow their businesses.",
    icon: (
      <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    title: "Gender Gap",
    description:
      "Women entrepreneurs continue to face disproportionate barriers to formal banking, credit history development, and business financing.",
    icon: (
      <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    title: "Refugee Exclusion",
    description:
      "Refugee entrepreneurs are frequently excluded from banking systems, limiting their ability to rebuild livelihoods with stability and dignity.",
    icon: (
      <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const metrics = [
  { value: "200M+", label: "Women in Africa" },
  { value: "400M+", label: "Youth Population" },
  { value: "5M+", label: "Refugees in Africa" },
  { value: "60%", label: "Youth Under Age 25" },
];

function Challenges() {
  const navigate = useNavigate();

  return (
    <section id="challenges" className="min-h-screen bg-white pt-28 pb-20 text-slate-900">
      <div className="container mx-auto px-4 md:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="border-b border-slate-200 pb-10">
            <span className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">
              The Challenge
            </span>
            <div className="mt-6 grid gap-8 lg:grid-cols-[1.3fr_0.7fr] lg:items-end">
              <div>
                <h1 className="text-4xl font-bold leading-tight text-slate-900 md:text-5xl">
                  Understanding the barriers that continue to limit financial inclusion.
                </h1>
                <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600">
                  When communities lack access to fair finance, opportunity stalls. Oriri Africa Fund focuses on
                  financing constraints, gender-based exclusion, and refugee access barriers that continue to affect
                  entrepreneurs across Rwanda and the wider region.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Strategic Priority
                </p>
                <p className="mt-3 text-3xl font-bold text-slate-900">Inclusive Access</p>
                <p className="mt-4 text-base leading-7 text-slate-600">
                  Addressing structural barriers that prevent underserved entrepreneurs from participating fully in the economy.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-8 py-12 md:grid-cols-3">
            {challengeItems.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                  {item.icon}
                </div>
                <h2 className="text-xl font-semibold text-slate-900">{item.title}</h2>
                <p className="mt-4 text-base leading-7 text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 md:p-10">
            <h2 className="text-2xl font-semibold text-slate-900">Context at a Glance</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {metrics.map((metric) => (
                <div key={metric.label} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                  <div className="text-4xl font-bold text-slate-900">{metric.value}</div>
                  <p className="mt-2 text-sm font-medium uppercase tracking-[0.12em] text-slate-500">{metric.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-900 px-8 py-10 text-white">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-300">
                  Next Step
                </p>
                <h2 className="mt-3 text-2xl font-semibold">
                  Explore how Oriri Africa Fund responds to these barriers.
                </h2>
              </div>
              <button
                onClick={() => navigate("/products")}
                className="inline-flex items-center justify-center rounded-full bg-white px-8 py-3 text-base font-semibold text-slate-900 transition-colors duration-200 hover:bg-slate-100"
              >
                <span className="mr-2">Our Solution</span>
                <svg className="inline w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Challenges;
